/**
 * Organizations Table Component
 *
 * Displays all organizations in a responsive table format with sortable columns.
 */

import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/ui/avatar';
import { Icon, IconName } from '@/icons';
import { Organization } from '@/types/orgs-management.types';
import { formatDate } from '@/utils/date';

export interface OrgsTableProps {
  /** Organizations to display in the table */
  organizations: Organization[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Callback when a row is clicked */
  onRowClick?: (org: Organization) => void;
  /** Callback when delete is clicked */
  onDelete?: (org: Organization) => void;
  /** Callback when restore is clicked (for soft-deleted orgs) */
  onRestore?: (org: Organization) => void;
}

type SortKey = 'code' | 'title' | 'created_at';
type SortDirection = 'asc' | 'desc';

/**
 * Loading skeleton for table rows
 */
function TableSkeleton() {
  return (
    <>
      {[1, 2, 3].map(i => (
        <TableRow key={i}>
          <TableCell>
            <div className="h-8 w-8 bg-muted animate-pulse rounded-full" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-16" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-40" />
          </TableCell>
          <TableCell>
            <div className="h-6 bg-muted animate-pulse rounded-full w-16" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-24" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

/**
 * Empty state when no organizations exist
 */
function EmptyState() {
  return (
    <TableRow>
      <TableCell colSpan={5} className="h-24 text-center">
        <div className="flex flex-col items-center justify-center text-muted-foreground">
          <p className="text-sm">No organizations found</p>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Format timestamp for display
 */

/**
 * Organizations Table Component
 *
 * Displays organizations in a table with sortable columns.
 */
export function OrgsTable({
  organizations,
  isLoading = false,
  onRowClick,
  onDelete,
  onRestore,
}: OrgsTableProps) {
  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: SortDirection;
  } | null>(null);

  // Handle sorting
  const handleSort = (key: SortKey) => {
    let direction: SortDirection = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort organizations
  const sortedOrganizations = [...organizations].sort((a, b) => {
    if (!sortConfig) return 0;

    const { key, direction } = sortConfig;
    const aValue = a[key] ?? '';
    const bValue = b[key] ?? '';

    if (aValue < bValue) {
      return direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Get sort indicator
  const getSortIndicator = (key: SortKey) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const handleRowClickInternal = (org: Organization) => {
    if (onRowClick) {
      onRowClick(org);
    }
  };

  return (
    <div className="rounded-lg border shadow-soft overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-left w-12"></TableHead>
            <TableHead
              className="cursor-pointer hover:!bg-accent/30 select-none text-left transition-colors duration-150"
              onClick={() => handleSort('code')}
            >
              Code{getSortIndicator('code')}
            </TableHead>
            <TableHead
              className="cursor-pointer hover:!bg-accent/30 select-none text-left transition-colors duration-150"
              onClick={() => handleSort('title')}
            >
              Title{getSortIndicator('title')}
            </TableHead>
            <TableHead className="text-left">Superadmin Org</TableHead>
            <TableHead
              className="cursor-pointer hover:!bg-accent/30 select-none text-left transition-colors duration-150"
              onClick={() => handleSort('created_at')}
            >
              Created At{getSortIndicator('created_at')}
            </TableHead>
            {(onDelete || onRestore) && (
              <TableHead className="w-12"></TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableSkeleton />
          ) : sortedOrganizations.length === 0 ? (
            <EmptyState />
          ) : (
            sortedOrganizations.map(org => {
              const isDeleted = !!org.deleted_at;
              return (
                <TableRow
                  key={org.id}
                  className={`cursor-pointer hover:!bg-accent/50 transition-colors duration-150 ${
                    isDeleted ? 'opacity-50' : ''
                  }`}
                  onClick={() => handleRowClickInternal(org)}
                >
                  <TableCell>
                    <Avatar
                      src={org.profile_image}
                      displayName={org.title}
                      userId={org.id}
                      size={32}
                    />
                  </TableCell>
                  <TableCell
                    className={`font-medium font-mono ${isDeleted ? 'line-through' : ''}`}
                  >
                    {org.code}
                  </TableCell>
                  <TableCell
                    className={`font-medium ${isDeleted ? 'line-through' : ''}`}
                  >
                    {org.title}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={org.is_superadmin_org ? 'purple' : 'default'}
                    >
                      {org.is_superadmin_org ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(org.created_at)}
                  </TableCell>
                  {(onDelete || onRestore) && (
                    <TableCell>
                      {isDeleted && onRestore ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-primary hover:bg-primary/10"
                          onClick={e => {
                            e.stopPropagation();
                            onRestore(org);
                          }}
                          title={`Restore ${org.title}`}
                        >
                          Restore
                        </Button>
                      ) : !isDeleted && onDelete ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-destructive/10"
                          onClick={e => {
                            e.stopPropagation();
                            onDelete(org);
                          }}
                          title={`Delete ${org.title}`}
                        >
                          <Icon
                            name={IconName.TRASH}
                            size="sm"
                            className="text-destructive/70"
                          />
                        </Button>
                      ) : null}
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
